import { useCallback, useEffect, useRef, useState } from 'react';

import {
  completeWhatsAppEmbeddedSignup,
  createWhatsAppOnboardingSession,
  getOfficialWhatsAppConfig,
  getWhatsAppEmbeddedSignupConfig,
  type OfficialWhatsAppConfig,
  type WhatsAppEmbeddedSignupConfig,
} from '../services/api.ts';


type FacebookAuthResponse = {
  code?: string;
  business_id?: string;
  businessId?: string;
  waba_id?: string;
  waba_ids?: string[];
  phone_number_id?: string;
  phone_number_ids?: string[];
  extras?: Record<string, unknown>;
  [key: string]: unknown;
};

type FacebookLoginResponse = {
  authResponse?: FacebookAuthResponse;
  status?: string;
  error?: unknown;
};

declare global {
  interface Window {
    FB?: {
      init: (options: {
        appId: string;
        autoLogAppEvents?: boolean;
        cookie: boolean;
        xfbml: boolean;
        version: string;
      }) => void;
      login: (
        callback: (response: FacebookLoginResponse) => void,
        options: Record<string, unknown>
      ) => void;
    };
    fbAsyncInit?: () => void;
  }
}

export type MetaEmbeddedSignupPhase =
  | 'preparing_sdk'
  | 'ready'
  | 'opening_meta'
  | 'awaiting_meta'
  | 'creating_session'
  | 'completing'
  | 'provisioning'
  | 'connected'
  | 'cancelled'
  | 'error';

export type MetaEmbeddedSignupReadiness = 'loading' | 'ready' | 'error';

export type MetaEmbeddedSignupFinishEvent =
  | 'FINISH'
  | 'FINISH_ONLY_WABA'
  | 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING'
  | 'FINISH_OBO_MIGRATION'
  | 'FINISH_GRANT_ONLY_API_ACCESS';

export type EmbeddedSignupAssets = {
  business_id?: string;
  waba_id?: string;
  phone_number_id?: string;
};

type NormalizedEmbeddedSignupMessage =
  | {
      kind: 'finish';
      event: MetaEmbeddedSignupFinishEvent;
      assets: EmbeddedSignupAssets;
    }
  | {
      kind: 'cancel';
      currentStep?: string;
    }
  | {
      kind: 'error';
      message?: string;
    };

type ActiveAttempt = {
  id: number;
  abortController: AbortController;
  code?: string;
  assets: EmbeddedSignupAssets;
  finishEvent?: MetaEmbeddedSignupFinishEvent;
  completionStarted: boolean;
  listener?: (event: MessageEvent) => void;
  timeoutId?: ReturnType<typeof window.setTimeout>;
};

type EmbeddedSignupCallbacks = {
  onConnected: (connection: OfficialWhatsAppConfig) => void;
  onConnectionSnapshot?: (connection: OfficialWhatsAppConfig) => void;
  onError: (message: string) => void;
  onCancelled?: (message: string) => void;
};

const FACEBOOK_SDK_SCRIPT_ID = 'facebook-jssdk';
const FACEBOOK_SDK_TIMEOUT_MS = 15_000;
const EMBEDDED_SIGNUP_TIMEOUT_MS = 9 * 60_000;
const FACEBOOK_ORIGIN_PATTERN = /^https:\/\/([a-z0-9-]+\.)*facebook\.com$/i;
const FINISH_EVENTS = new Set<MetaEmbeddedSignupFinishEvent>([
  'FINISH',
  'FINISH_ONLY_WABA',
  'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING',
  'FINISH_OBO_MIGRATION',
  'FINISH_GRANT_ONLY_API_ACCESS',
]);

let facebookSdkPromise: Promise<void> | null = null;
let initializedFacebookSdkKey: string | null = null;

const asRecord = (value: unknown): Record<string, any> => (
  value && typeof value === 'object' ? value as Record<string, any> : {}
);

const firstString = (...values: unknown[]): string | undefined => {
  for (const value of values) {
    if (typeof value === 'string' || typeof value === 'number') {
      const normalized = String(value).trim();
      if (normalized) return normalized;
    }
  }
  return undefined;
};

const firstArrayValue = (value: unknown): unknown => (
  Array.isArray(value) && value.length > 0 ? value[0] : undefined
);

const safeMetaMessage = (value: unknown): string | undefined => {
  const message = firstString(value)?.replace(/\s+/g, ' ').trim();
  return message ? message.slice(0, 300) : undefined;
};

export const normalizeEmbeddedSignupAssets = (...values: unknown[]): EmbeddedSignupAssets => {
  const records = values.map(asRecord);
  return {
    business_id: firstString(...records.flatMap((record) => [
      record.business_id,
      record.businessId,
      record.business_manager_id,
      record.businessManagerId,
    ])),
    waba_id: firstString(...records.flatMap((record) => [
      record.waba_id,
      record.wabaId,
      record.whatsapp_business_account_id,
      record.whatsappBusinessAccountId,
      firstArrayValue(record.waba_ids),
    ])),
    phone_number_id: firstString(...records.flatMap((record) => [
      record.phone_number_id,
      record.phoneNumberId,
      record.phone_id,
      record.phoneId,
      firstArrayValue(record.phone_number_ids),
    ])),
  };
};

const mergeAssets = (
  current: EmbeddedSignupAssets,
  incoming: EmbeddedSignupAssets
): EmbeddedSignupAssets => ({
  business_id: incoming.business_id || current.business_id,
  waba_id: incoming.waba_id || current.waba_id,
  phone_number_id: incoming.phone_number_id || current.phone_number_id,
});

export const normalizeEmbeddedSignupMessage = (
  event: Pick<MessageEvent, 'origin' | 'data'>
): NormalizedEmbeddedSignupMessage | null => {
  if (!FACEBOOK_ORIGIN_PATTERN.test(event.origin)) return null;

  let payload: unknown = event.data;
  if (typeof payload === 'string') {
    try {
      payload = JSON.parse(payload);
    } catch {
      return null;
    }
  }

  const root = asRecord(payload);
  const rootData = asRecord(root.data);
  if (root.type !== 'WA_EMBEDDED_SIGNUP' && rootData.type !== 'WA_EMBEDDED_SIGNUP') {
    return null;
  }

  const eventName = firstString(root.event, rootData.event)?.toUpperCase();
  const result = asRecord(rootData.data);
  const dataSources = [
    result,
    rootData,
    root,
    asRecord(result.session_info),
    asRecord(rootData.session_info),
  ];

  if (eventName && FINISH_EVENTS.has(eventName as MetaEmbeddedSignupFinishEvent)) {
    return {
      kind: 'finish',
      event: eventName as MetaEmbeddedSignupFinishEvent,
      assets: normalizeEmbeddedSignupAssets(...dataSources),
    };
  }

  const error = asRecord(result.error || rootData.error || root.error);
  const errorMessage = safeMetaMessage(
    result.error_message
    || rootData.error_message
    || root.error_message
    || error.message
  );
  const errorCode = firstString(
    result.error_code,
    rootData.error_code,
    root.error_code,
    error.code
  );

  if (eventName === 'ERROR' || (eventName === 'CANCEL' && (errorMessage || errorCode))) {
    return {
      kind: 'error',
      message: errorMessage,
    };
  }

  if (eventName === 'CANCEL') {
    return {
      kind: 'cancel',
      currentStep: safeMetaMessage(
        result.current_step
        || rootData.current_step
        || root.current_step
      ),
    };
  }

  return null;
};

const readableErrorText = (value: unknown): string | undefined => {
  if (typeof value === 'string') {
    const normalized = value.replace(/\s+/g, ' ').trim();
    return normalized ? normalized.slice(0, 500) : undefined;
  }
  if (Array.isArray(value)) {
    const messages = value
      .map((item) => readableErrorText(asRecord(item).msg || item))
      .filter(Boolean);
    return messages.length ? messages.join(', ').slice(0, 500) : undefined;
  }
  const record = asRecord(value);
  return readableErrorText(record.message || record.msg);
};

const extractErrorMessage = (error: any, fallback: string): string => (
  readableErrorText(error?.response?.data?.detail)
  || readableErrorText(error?.message)
  || fallback
);

const cancelledStepLabel = (currentStep?: string): string | undefined => {
  if (!currentStep) return undefined;
  const labels: Record<string, string> = {
    BUSINESS_SELECTION: 'seleção da empresa',
    BUSINESS_VERIFICATION: 'verificação da empresa',
    WABA_SELECTION: 'seleção da conta do WhatsApp',
    WABA_SETUP: 'configuração da conta do WhatsApp',
    PHONE_NUMBER_SELECTION: 'seleção do número',
    PHONE_NUMBER_SETUP: 'configuração do número',
    PROFILE_SETUP: 'configuração do perfil',
    PERMISSIONS: 'revisão das permissões',
  };
  return labels[currentStep.trim().toUpperCase()];
};

const isConnectedSnapshot = (connection: OfficialWhatsAppConfig): boolean => (
  connection.has_official
  || connection.status === 'connected'
  || connection.status === 'legacy_connected'
);

const isProvisioningSnapshot = (connection: OfficialWhatsAppConfig): boolean => (
  connection.status === 'provisioning'
);

const removeFailedFacebookScript = () => {
  const existing = document.getElementById(FACEBOOK_SDK_SCRIPT_ID);
  if (existing && !window.FB) existing.remove();
};

export const loadFacebookSdk = (
  appId: string,
  version: string
): Promise<void> => {
  const sdkKey = `${appId}:${version}`;

  const initialize = () => {
    if (!window.FB) throw new Error('O SDK da Meta não ficou disponível.');
    if (initializedFacebookSdkKey === sdkKey) return;
    window.FB.init({
      appId,
      autoLogAppEvents: true,
      cookie: true,
      xfbml: false,
      version,
    });
    initializedFacebookSdkKey = sdkKey;
  };

  if (window.FB) {
    try {
      initialize();
      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    }
  }

  if (facebookSdkPromise) return facebookSdkPromise;

  let pendingPromise: Promise<void>;
  pendingPromise = new Promise<void>((resolve, reject) => {
    let settled = false;
    let script = document.getElementById(FACEBOOK_SDK_SCRIPT_ID) as HTMLScriptElement | null;
    const previousAsyncInit = window.fbAsyncInit;

    const cleanup = () => {
      window.clearTimeout(timeoutId);
      script?.removeEventListener('load', handleLoad);
      script?.removeEventListener('error', handleError);
      if (window.fbAsyncInit === handleAsyncInit) {
        if (previousAsyncInit) {
          window.fbAsyncInit = previousAsyncInit;
        } else {
          delete window.fbAsyncInit;
        }
      }
    };

    const settleReady = () => {
      if (settled) return;
      try {
        initialize();
        settled = true;
        cleanup();
        resolve();
      } catch (error) {
        settleError(error);
      }
    };

    const settleError = (error: unknown) => {
      if (settled) return;
      settled = true;
      cleanup();
      removeFailedFacebookScript();
      reject(error);
    };

    function handleLoad() {
      settleReady();
    }

    function handleError() {
      settleError(new Error('Não foi possível carregar o SDK da Meta.'));
    }

    function handleAsyncInit() {
      settleReady();
    }

    const timeoutId = window.setTimeout(
      () => settleError(new Error('O SDK da Meta demorou para responder. Tente novamente.')),
      FACEBOOK_SDK_TIMEOUT_MS
    );

    window.fbAsyncInit = handleAsyncInit;

    if (!script) {
      script = document.createElement('script');
      script.id = FACEBOOK_SDK_SCRIPT_ID;
      script.async = true;
      script.defer = true;
      script.crossOrigin = 'anonymous';
      script.src = 'https://connect.facebook.net/pt_BR/sdk.js';
      document.body.appendChild(script);
    }

    script.addEventListener('load', handleLoad);
    script.addEventListener('error', handleError);
  });

  facebookSdkPromise = pendingPromise;
  void pendingPromise.catch(() => {
    if (facebookSdkPromise === pendingPromise) facebookSdkPromise = null;
  });
  return pendingPromise;
};

const buildFacebookLoginOptions = (config: WhatsAppEmbeddedSignupConfig) => {
  const extras: Record<string, unknown> = {
    setup: config.setup || {},
  };
  if (config.feature) extras.feature = config.feature;
  if (config.version) extras.version = config.version;
  if (config.session_info_version) {
    extras.sessionInfoVersion = config.session_info_version;
  }
  if (config.feature_type) extras.featureType = config.feature_type;

  return {
    config_id: config.config_id,
    auth_type: 'rerequest',
    response_type: 'code',
    override_default_response_type: true,
    extras,
  };
};

export const isMetaEmbeddedSignupBusy = (phase: MetaEmbeddedSignupPhase): boolean => (
  phase === 'opening_meta'
  || phase === 'awaiting_meta'
  || phase === 'creating_session'
  || phase === 'completing'
);

export const useWhatsAppEmbeddedSignup = ({
  onConnected,
  onConnectionSnapshot,
  onError,
  onCancelled,
}: EmbeddedSignupCallbacks) => {
  const [readiness, setReadiness] = useState<MetaEmbeddedSignupReadiness>('loading');
  const [phase, setPhase] = useState<MetaEmbeddedSignupPhase>('preparing_sdk');
  const [preparationError, setPreparationError] = useState('');
  const [sdkConfig, setSdkConfig] = useState<WhatsAppEmbeddedSignupConfig | null>(null);

  const mountedRef = useRef(true);
  const callbacksRef = useRef<EmbeddedSignupCallbacks>({
    onConnected,
    onConnectionSnapshot,
    onError,
    onCancelled,
  });
  const preparePromiseRef = useRef<Promise<void> | null>(null);
  const activeAttemptRef = useRef<ActiveAttempt | null>(null);
  const attemptSequenceRef = useRef(0);
  const cancelActiveAttemptRef = useRef<(notify?: boolean) => void>(() => undefined);

  callbacksRef.current = {
    onConnected,
    onConnectionSnapshot,
    onError,
    onCancelled,
  };

  const prepare = useCallback((): Promise<void> => {
    if (preparePromiseRef.current) return preparePromiseRef.current;

    setReadiness('loading');
    setPhase('preparing_sdk');
    setPreparationError('');

    const promise = getWhatsAppEmbeddedSignupConfig()
      .then(async (config) => {
        await loadFacebookSdk(config.app_id, config.graph_api_version);
        if (!mountedRef.current) return;
        setSdkConfig(config);
        setReadiness('ready');
        setPhase('ready');
      })
      .catch((error) => {
        if (!mountedRef.current) return;
        setSdkConfig(null);
        setReadiness('error');
        setPhase('error');
        setPreparationError(extractErrorMessage(
          error,
          'Não foi possível preparar a conexão com a Meta.'
        ));
      })
      .finally(() => {
        if (preparePromiseRef.current === promise) preparePromiseRef.current = null;
      });

    preparePromiseRef.current = promise;
    return promise;
  }, []);

  const connect = useCallback(() => {
    cancelActiveAttemptRef.current(false);

    if (readiness !== 'ready' || !sdkConfig || !window.FB) {
      const message = preparationError || 'A conexão com a Meta ainda não está pronta.';
      callbacksRef.current.onError(message);
      void prepare();
      return;
    }

    const attempt: ActiveAttempt = {
      id: ++attemptSequenceRef.current,
      abortController: new AbortController(),
      assets: {},
      completionStarted: false,
    };
    activeAttemptRef.current = attempt;

    const isCurrent = () => (
      mountedRef.current
      && activeAttemptRef.current === attempt
      && !attempt.abortController.signal.aborted
    );

    const cleanupBrowserResources = () => {
      if (attempt.listener) {
        window.removeEventListener('message', attempt.listener);
        attempt.listener = undefined;
      }
      if (attempt.timeoutId) {
        window.clearTimeout(attempt.timeoutId);
        attempt.timeoutId = undefined;
      }
    };

    const closeAttempt = () => {
      cleanupBrowserResources();
      if (!attempt.abortController.signal.aborted) attempt.abortController.abort();
      if (activeAttemptRef.current === attempt) activeAttemptRef.current = null;
    };

    const completeAttempt = (
      connection: OfficialWhatsAppConfig,
      nextPhase: 'connected' | 'provisioning'
    ) => {
      if (!isCurrent()) return;
      cleanupBrowserResources();
      if (activeAttemptRef.current === attempt) activeAttemptRef.current = null;
      setPhase(nextPhase);
      callbacksRef.current.onConnected(connection);
    };

    const failAttempt = (message: string, cancelled = false) => {
      if (!isCurrent()) return;
      closeAttempt();
      if (!mountedRef.current) return;
      setPhase(cancelled ? 'cancelled' : 'error');
      if (cancelled) {
        callbacksRef.current.onCancelled?.(message);
      } else {
        callbacksRef.current.onError(message);
      }
    };

    const runCompletion = async () => {
      if (!isCurrent() || attempt.completionStarted) return;
      attempt.completionStarted = true;
      cleanupBrowserResources();
      setPhase('creating_session');

      try {
        const onboarding = await createWhatsAppOnboardingSession(
          attempt.abortController.signal
        );
        if (!isCurrent()) return;

        setPhase('completing');
        const payload = {
          session_id: onboarding.session_id,
          session_token: onboarding.session_token,
          code: attempt.code as string,
          waba_id: attempt.assets.waba_id as string,
          phone_number_id: attempt.assets.phone_number_id as string,
          ...(attempt.assets.business_id
            ? { business_id: attempt.assets.business_id }
            : {}),
        };
        const connection = await completeWhatsAppEmbeddedSignup(
          payload,
          attempt.abortController.signal
        );
        if (!isCurrent()) return;

        if (isConnectedSnapshot(connection)) {
          completeAttempt(connection, 'connected');
          return;
        }
        if (isProvisioningSnapshot(connection)) {
          completeAttempt(connection, 'provisioning');
          return;
        }

        callbacksRef.current.onConnectionSnapshot?.(connection);
        failAttempt(
          connection.last_error
          || 'A Meta recebeu a autorização, mas a conexão ainda precisa de atenção.'
        );
      } catch (error: any) {
        if (!isCurrent()) return;
        try {
          const snapshot = await getOfficialWhatsAppConfig();
          if (isCurrent() && isConnectedSnapshot(snapshot)) {
            completeAttempt(snapshot, 'connected');
            return;
          }
          if (isCurrent() && isProvisioningSnapshot(snapshot)) {
            completeAttempt(snapshot, 'provisioning');
            return;
          }
          if (isCurrent()) callbacksRef.current.onConnectionSnapshot?.(snapshot);
        } catch {
          // A falha original da conclusão continua sendo a mensagem principal.
        }
        failAttempt(extractErrorMessage(
          error,
          'Não foi possível concluir a conexão com a Meta.'
        ));
      }
    };

    const maybeComplete = () => {
      if (!isCurrent() || !attempt.code || !attempt.finishEvent) return;

      if (attempt.finishEvent === 'FINISH_ONLY_WABA' && !attempt.assets.phone_number_id) {
        failAttempt(
          'A Meta compartilhou a conta, mas o fluxo terminou sem selecionar um número. Tente novamente e conclua a etapa do telefone.'
        );
        return;
      }

      if (!attempt.assets.waba_id || !attempt.assets.phone_number_id) {
        failAttempt(
          'A Meta concluiu a autorização sem identificar a conta e o número do WhatsApp. Tente novamente e conclua todas as etapas.'
        );
        return;
      }

      void runCompletion();
    };

    attempt.listener = (messageEvent: MessageEvent) => {
      if (!isCurrent()) return;
      const normalized = normalizeEmbeddedSignupMessage(messageEvent);
      if (!normalized) return;

      if (normalized.kind === 'cancel') {
        const stepLabel = cancelledStepLabel(normalized.currentStep);
        const step = stepLabel ? ` durante a ${stepLabel}` : '';
        failAttempt(`A conexão com a Meta foi cancelada${step}.`, true);
        return;
      }

      if (normalized.kind === 'error') {
        failAttempt(
          'A Meta não conseguiu concluir a conexão. Revise as etapas de autorização e tente novamente.'
        );
        return;
      }

      attempt.finishEvent = normalized.event;
      attempt.assets = mergeAssets(attempt.assets, normalized.assets);
      setPhase('awaiting_meta');
      maybeComplete();
    };

    window.addEventListener('message', attempt.listener);
    attempt.timeoutId = window.setTimeout(() => {
      failAttempt(
        'O tempo para concluir a autorização da Meta expirou. Tente novamente.'
      );
    }, EMBEDDED_SIGNUP_TIMEOUT_MS);

    cancelActiveAttemptRef.current = (notify = false) => {
      if (
        activeAttemptRef.current !== attempt
        || attempt.abortController.signal.aborted
      ) return;
      closeAttempt();
      if (notify && mountedRef.current) {
        setPhase('cancelled');
        callbacksRef.current.onCancelled?.('A conexão com a Meta foi cancelada.');
      }
    };

    setPhase('opening_meta');
    try {
      window.FB.login((response) => {
        if (!isCurrent()) return;

        const authResponse = response.authResponse;
        const code = firstString(authResponse?.code);
        if (!code) {
          failAttempt('A autorização da Meta foi cancelada ou não foi concluída.', true);
          return;
        }

        attempt.code = code;
        attempt.assets = mergeAssets(
          attempt.assets,
          normalizeEmbeddedSignupAssets(
            authResponse,
            authResponse?.extras,
            asRecord(authResponse).data
          )
        );
        setPhase('awaiting_meta');
        maybeComplete();
      }, buildFacebookLoginOptions(sdkConfig));
    } catch (error) {
      failAttempt(extractErrorMessage(
        error,
        'Não foi possível abrir a conexão com a Meta.'
      ));
    }
  }, [preparationError, prepare, readiness, sdkConfig]);

  const cancel = useCallback(() => {
    cancelActiveAttemptRef.current(true);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    void prepare();
    return () => {
      mountedRef.current = false;
      cancelActiveAttemptRef.current(false);
    };
  }, [prepare]);

  return {
    readiness,
    phase,
    preparationError,
    isBusy: isMetaEmbeddedSignupBusy(phase),
    connect,
    cancel,
    prepare,
  };
};
